import { AgGridReact } from 'ag-grid-react';
import { Checkbox, FormControlLabel, InputAdornment } from '@mui/material';
import { LexoRank } from 'lexorank';
import { useCallback, useState, useEffect, useRef, useContext } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import TextField from '@mui/material/TextField';
import WrapTextIcon from '@mui/icons-material/WrapText';

import { ADD_DEPENDENCY, ADD_EPIC, DELETE_DEPENDENCY, GET_EPICS, UPDATE_EPIC, UPDATE_EPIC_SKILL, UPDATE_SEQUENCE } from '../../apis/epic';
import { GET_SKILL_MEMBER } from '../../apis/member';
import { SHOW_TIMELINE_PROMPT } from '../../constants/common';
import AddDependency from './AddDependency';
import AlertTeamMembersDialog from './alertTeamMembersDialog';
import Button from '../atoms/button';
import deleteIconButton from './deleteEpic';
import DependenciesRenderer from './DependenciesRenderer';
import NumericEditor from './numericCellEditor';
import ProjectContext from '../../context/ProjectContext';
import SprintTeamMemberRenderer from './sprintMemberRenderer';
import StatusRenderer from './statusRenderer';
import Textarea from './Textarea';
import useLoader from '../../context/LoaderContext';
import useProjectVersionHistory from '../../context/VersionHistoryContext';
import useWorkspace from '../../context/WorkspaceContext';

const propTypes = {
  projectId: PropTypes.string,
  projectKey: PropTypes.string.isRequired,
};

const defaultProps = {
  projectId: 0,
};

const MIN_LEXO_RANK = LexoRank.min().genNext().toString();
const CONTAINER_STYLE = { width: '100%', height: '65vh' };
const ROW_HEIGHT = 42;

const EpicList = (props) => {
  // init
  const { projectId, projectKey } = props;
  const { isLoading, setIsLoading } = useLoader();
  const { startDate, duration } = useContext(ProjectContext);
  const { showEpicTour, setEpicTour } = useWorkspace();
  const { setUndoEdits, selectedListItem, showVersionHistoryBar, readOnlyMode, undoEdits, undo, redo } = useProjectVersionHistory();
  const params = useParams();
  const checkboxRef = useRef(false);

  // states
  const [open, setOpen] = useState(false);
  const [popup, setPopup] = useState(false);
  const [currentSkillCellEvent, setCurrentSkillCellEvent] = useState(null);
  const [currentRank, setCurrentRank] = useState(null);
  const [showAddButton, setShowAddButton] = useState(true);
  const [btnName, setBtnName] = useState('Create Epic');
  const [errorMessage, setErrorMessage] = useState('');
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState({});
  const [addDependsValue, setAddDependsValue] = useState([]);
  const [DeleteDepends, setDeleteDepends] = useState([]);
  const [fetch, setFetch] = useState(false);
  const [sequenceData, setSequenceData] = useState({ activeIndex: -1, prevGridData: [] });
  const [skillList, setSkillList] = useState([]);
  const [, setIsValidDragEnd] = useState(false);
  const [, setIsDragStarted] = useState(false);
  const [editEpicData, setEditEpicData] = useState({});
  const [wrapText, setWrapText] = useState(localStorage.getItem('WrapText') === 'true');
  const [openDialog, setOpenDialog] = useState(false);
  const [teamMemberMessage, setTeamMemberMessage] = useState('');
  const [showTimelinePrompt, setShowTimelinePrompt] = useState(false);
  const [trigger, setTrigger] = useState(false);

  // mutations/queries
  const [addDependency] = useMutation(ADD_DEPENDENCY);
  const [deleteDependency] = useMutation(DELETE_DEPENDENCY);
  const [addEpic] = useMutation(ADD_EPIC);
  const [updateSequence] = useMutation(UPDATE_SEQUENCE);
  const [updateEpic] = useMutation(UPDATE_EPIC);
  const [updateEpicSkills] = useMutation(UPDATE_EPIC_SKILL);
  const [skillMembers] = useLazyQuery(GET_SKILL_MEMBER, { fetchPolicy: 'no-cache' });
  const [getEpics] = useLazyQuery(GET_EPICS, { fetchPolicy: 'no-cache' });

  // constants
  const isDragStartedRef = useRef(false);
  const gridRef = useRef();
  const isValidDragEndRef = useRef(false);
  const customWidth = JSON.parse(localStorage.getItem('Width'));
  const isHidden = showVersionHistoryBar && readOnlyMode && selectedListItem !== null && selectedListItem !== undefined;
  const initialColumnDefs = [
    { headerName: 'Id', field: 'id', hide: true },
    { headerName: 'Sequence', field: 'sequence', hide: true },
    { headerName: 'IsActive', field: 'isActive', hide: true },
    { headerName: 'No', field: 'index', cellRenderer: deleteIconButton, resizable: true, cellEditorParams: { stateProps: () => setFetch(!fetch) }, valueGetter: 'node.rowIndex + 1', rowDrag: () => !isHidden, suppressSizeToFit: true, width: customWidth ? customWidth.index : 110, headerClass: 'custom-first-cell', cellClass: 'first-cell' },
    { headerName: 'Epic Id', field: 'epicId', resizable: true, headerClass: 'parent-style', width: customWidth ? customWidth.epicId : 120 },
    { headerName: 'Summary', field: 'summary', wrapText, autoHeight: true, editable: !isHidden, resizable: true, headerClass: 'parent-style', singleClickEdit: !isHidden, cellEditorPopup: true, cellEditor: Textarea, width: customWidth ? customWidth.summary : 280, cellEditorParams: { onChangeValue: (epicData) => setEditEpicData(epicData), type: 'summary' } },
    { headerName: 'Description', field: 'description', wrapText, autoHeight: true, editable: !isHidden, singleClickEdit: !isHidden, cellEditorPopup: true, resizable: true, headerClass: 'parent-style', width: customWidth ? customWidth.description : 415, cellEditor: Textarea, cellEditorParams: { onChangeValue: (epicData) => setEditEpicData(epicData), type: 'description' } },
    { headerName: 'Dependency Epic', field: 'dependency', cellEditor: AddDependency, cellEditorPopup: true, editable: !isHidden, singleClickEdit: !isHidden, resizable: true, wrapText, autoHeight: true, headerClass: 'parent-style', cellEditorParams: { onAutoCompleteChange: (value) => setAddDependsValue(value), OnDeleteDependency: (value) => setDeleteDepends(value.map((dependency) => Number(dependency.id))) }, cellRenderer: DependenciesRenderer, width: customWidth ? customWidth.dependency : 200, cellRendererParams: { projectKey } },
  ];

  const [columnDefs, setColumnDefs] = useState(initialColumnDefs);

  const updateEpicSkillData = async () => {
    setIsLoading(true);
    const sprintColumn = `${currentSkillCellEvent.colDef.parentHeader}Sprints`;
    const teamMemberColumn = `${currentSkillCellEvent.colDef.parentHeader}Teammembers`;
    const updateEpicSkill = {
      epicId: parseInt(currentSkillCellEvent.data.id, 10),
      skillId: parseInt(currentSkillCellEvent.colDef.id, 10),
      projectId: parseInt(projectId, 10),
      duration: parseInt(currentSkillCellEvent.data[sprintColumn], 10),
      team_members: !Number.isNaN(parseInt(currentSkillCellEvent.data[teamMemberColumn], 10))
        ? parseInt(currentSkillCellEvent.data[teamMemberColumn], 10) : 1,
    };

    const variables = { input: updateEpicSkill };
    await updateEpicSkills({ variables });

    if (!undoEdits) setUndoEdits(true);

    if (gridApi && currentSkillCellEvent.data[sprintColumn] && currentSkillCellEvent.data[teamMemberColumn] === undefined) {
      const colId = `${currentSkillCellEvent.colDef.field}`;
      const teamColId = `${colId.replace(/Sprints/gi, '')}Teammembers`;
      const newRowData = gridApi.getDisplayedRowAtIndex(currentSkillCellEvent.node.rowIndex).data;
      newRowData[colId] = parseInt(currentSkillCellEvent.data[sprintColumn], 10);

      newRowData[teamColId] = !Number.isNaN(
        parseInt(currentSkillCellEvent.data[teamMemberColumn], 10),
      ) ? parseInt(currentSkillCellEvent.data[teamMemberColumn], 10) : 1;
      gridApi.applyTransaction({ update: [newRowData] });
    }
    setIsLoading(false);
    setAddDependsValue([]);
  };

  const handleClickOpen = async () => {
    updateEpicSkillData();
    const checkboxValue = checkboxRef.current ? checkboxRef.current.checked : false;
    localStorage.setItem(SHOW_TIMELINE_PROMPT, JSON.stringify(checkboxValue || showTimelinePrompt));
    setShowTimelinePrompt(checkboxValue || showTimelinePrompt);
    setOpen(false);
  };

  const setEpicListData = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const { error, loading, data } = await getEpics({
        variables: {
          projectId: params.id,
          ProjectSkillUsersProjectId: parseInt(params.id, 10),
          input: { projectId: params.id },
          isActive: true,
          order: [
            {
              key: 'sequence',
              value: 'asc',
            },
          ],
        },
      });

      if (!error && !loading && data?.epics?.epics) {
        if (data?.epics?.epics.length > 0) {
          setBtnName('Create Epic');
          let arrayUniqueByKey = [];
          let dynamicColdefs = [];
          if (data?.getProjectSkillUsers) {
            if (data?.getProjectSkillUsers?.length > 0) {
              const skills = [];
              dynamicColdefs = Object.values(data.getProjectSkillUsers).map((s) => {
                const fieldName = s.skill.name.replaceAll(/\s/g, '').toLowerCase();
                const parentId = s.skill.id;
                const col = {
                  id: parentId,
                  headerName: s.skill.name,
                  headerClass: 'custom-parent-style',
                  field: fieldName,
                  children: [
                    {
                      field: `${fieldName}Sprints`,
                      id: parentId,
                      parentHeader: fieldName,
                      headerName: 'Sprints',
                      singleClickEdit: true,
                      headerClass: 'children-style',
                      cellRenderer: SprintTeamMemberRenderer,
                      width: 100,
                      cellEditor: NumericEditor,
                      editable: true,
                    },
                    {
                      id: parentId,
                      field: `${fieldName}Teammembers`,
                      headerName: 'Team Members',
                      headerClass: 'children-style',
                      parentHeader: fieldName,
                      singleClickEdit: true,
                      cellRenderer: SprintTeamMemberRenderer,
                      cellEditor: NumericEditor,
                      width: 100,
                      editable: true,
                      cellClass: 'child-column-border',
                    },
                  ],
                };
                skills.push(`${fieldName}Sprints`);
                skills.push(`${fieldName}Teammembers`);
                return col;
              });
              const fullstackIndex = dynamicColdefs.findIndex((d) => d.field === 'fullstack');
              dynamicColdefs = dynamicColdefs.filter((d) => d.field !== 'fullstack');

              if (fullstackIndex !== -1) {
                const backEndIndex = dynamicColdefs.findIndex((d) => d.field === 'backend');
                if (backEndIndex === -1) {
                  const backEndCol = {
                    id: 4,
                    headerName: 'Back End',
                    field: 'backend',
                    editable: true,
                    headerClass: 'custom-parent-style',
                    singleClickEdit: true,
                    cellEditor: NumericEditor,
                    width: 100,
                    children: [
                      {
                        field: 'backendSprints',
                        id: 4,
                        parentHeader: 'backend',
                        headerName: 'Sprints',
                        singleClickEdit: !isHidden,
                        headerClass: 'children-style',
                        cellRenderer: SprintTeamMemberRenderer,
                        width: 100,
                        cellEditor: NumericEditor,
                        editable: !isHidden,
                      },
                      {
                        id: 4,
                        field: 'backendTeammembers',
                        headerName: 'Team Members',
                        headerClass: 'children-style',
                        parentHeader: 'backend',
                        singleClickEdit: !isHidden,
                        cellRenderer: SprintTeamMemberRenderer,
                        cellEditor: NumericEditor,
                        width: 100,
                        editable: !isHidden,
                        cellClass: 'child-column-border',
                      },
                    ],
                  };
                  dynamicColdefs.push(backEndCol);
                  skills.push(backEndCol.children[0].field);
                  skills.push(backEndCol.children[1].field);
                }
                const frontEndIndex = dynamicColdefs.findIndex((d) => d.field === 'frontend');

                if (frontEndIndex === -1) {
                  const frontEndCol = {
                    id: 8,
                    headerName: 'Front End',
                    field: 'frontend',
                    editable: !isHidden,
                    headerClass: 'custom-parent-style',
                    singleClickEdit: !isHidden,
                    cellEditor: NumericEditor,
                    width: 100,
                    children: [
                      {
                        field: 'frontendSprints',
                        id: 8,
                        parentHeader: 'frontend',
                        headerName: 'Sprints',
                        singleClickEdit: !isHidden,
                        headerClass: 'children-style',
                        cellRenderer: SprintTeamMemberRenderer,
                        width: 100,
                        cellEditor: NumericEditor,
                        editable: !isHidden,
                      },
                      {
                        id: 8,
                        field: 'frontendTeammembers',
                        headerName: 'Team Members',
                        headerClass: 'children-style',
                        parentHeader: 'frontend',
                        singleClickEdit: true,
                        cellRenderer: SprintTeamMemberRenderer,
                        cellEditor: NumericEditor,
                        width: 100,
                        editable: true,
                        cellClass: 'child-column-border',
                      },
                    ],
                  };
                  dynamicColdefs.push(frontEndCol);
                  skills.push(frontEndCol.children[0].field);
                  skills.push(frontEndCol.children[1].field);
                }
              }
              setSkillList(skills);
              const key = 'field';
              arrayUniqueByKey = [...new Map(dynamicColdefs.map((item) => [item[key], item])).values()];

              if (startDate !== null && duration !== null) {
                const statusColumn = {
                  headerName: 'Status',
                  field: 'status',
                  width: 120,
                  headerClass: 'parent-style',
                  cellRenderer: StatusRenderer,
                };
                const statusColumnIndex = initialColumnDefs.findIndex((col) => col.field === 'status');

                if (statusColumnIndex === -1) {
                  initialColumnDefs.splice(5, 0, statusColumn);
                }
              }
              const updatedColumnDef = [...initialColumnDefs, ...arrayUniqueByKey];
              gridRef?.current?.api?.setColumnDefs(updatedColumnDef);
            } else {
              gridRef?.current?.api?.setColumnDefs(initialColumnDefs);
            }
          }
          const epicSkillDuration = data?.getEpicSkill;

          const epics = data?.epics?.epics.map((row, index) => {
            const epic = {
              epicId: `${projectKey}-${row.epicId.toString().padStart(3, '0')}`,
              summary: row.summary,
              description: row.description,
              index: index + 1,
              dependency: row.dependencies,
              sequence: row.sequence,
              status: row.status,
              id: row.id,
              isActive: row.isActive,
            };
            arrayUniqueByKey.forEach((es) => {
              const rowEpicId = parseInt(row.id, 10);
              const rowSkillId = parseInt(es.id, 10);

              epicSkillDuration.forEach((skill) => {
                if (skill.epicId === rowEpicId && skill.skillId === rowSkillId) {
                  epic[es.children[0].field] = skill.duration;
                  epic[es.children[1].field] = skill.team_members;
                }
              });
            });
            return epic;
          });
          setRowData(epics);
        } else {
          setRowData([]);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setEpicListData();
  }, [fetch, startDate, duration, selectedListItem, undo, redo]);

  useEffect(() => {
    document.body.classList.add('body-epic-list');
    return () => document.body.classList.remove('body-epic-list');
  }, []);

  const handleClickAway = () => setShowAddButton(true);

  const onAddButtonClick = () => setShowAddButton(false);

  const onSummaryKeyPress = async (e) => {
    setErrorMessage('');
    if (e.key === 'Enter') {
      setBtnName('Create Epic');
      if (e.target.value.length !== 0) {
        setShowAddButton(true);
        if (!isLoading) setIsLoading(true);
        const rank = gridApi.getDisplayedRowAtIndex(gridApi.getLastDisplayedRow());
        const sequence = rank ? LexoRank.parse(rank.data.sequence).genNext().toString() : MIN_LEXO_RANK;

        const variables = { input: { projectId, summary: e.target.value, sequence } };

        const { data: { addEpic: { id, epicId } } } = await addEpic({ variables });
        const epicValue = '000';
        const ans = epicValue.substring(0, epicValue.length - epicId.length) + epicId;
        gridApi.applyTransaction({
          add: [
            {
              id,
              epicId: `${projectKey}-${ans}`,
              summary: e.target.value,
              index: rowData.length + 1,
              sequence,
            },
          ],
        });
        setFetch(!fetch);
        const addEpicList = [];

        gridApi.forEachNode((node, index) => {
          const currentNode = {
            epicId: node.data.epicId,
            summary: node.data.summary,
            description: node.data.description,
            index: index + 1,
            dependency: node.data.dependencies,
            sequence: node.data.sequence,
            id: node.data.id,
          };
          addEpicList.push(currentNode);
        });
        setRowData(addEpicList);
        if (!undoEdits) setUndoEdits(true);
      } else {
        setErrorMessage('Summary required!');
      }
    }
  };
  const onSummaryKeyUp = (e) => {
    if (e.key === 'Escape') {
      setShowAddButton(!showAddButton);
    }
  };

  const showSummary = () => {
    setWrapText(!wrapText);
  };

  useEffect(() => {
    localStorage.setItem('WrapText', wrapText);
    if (Object.keys(gridApi).length > 0) {
      setColumnDefs(
        gridApi.getColumnDefs().map((col) => {
          if ('wrapText' in col) {
            return { ...col, wrapText };
          }
          return col;
        }),
      );
    }
  }, [wrapText]);

  const onFilterTextBoxChanged = (e) => {
    const dataFilter = e.target.value;
    gridApi.setQuickFilter(dataFilter);
  };

  const dialogClose = () => {
    setTeamMemberMessage('');
    gridRef.current.api.undoCellEditing();
    setOpenDialog(false);
    setOpen(true);
  };

  const onAddDependencyEpic = async (e) => {
    const addDep = addDependsValue.map((dependency) => Number(dependency.id));
    const addDependencyEpic = { epicId: parseInt(e.data.id, 10), dependsOn: addDep };

    const variables = { input: addDependencyEpic };

    const addDependencyResult = await addDependency({ variables });

    const addDependencyList = [];
    addDependencyResult.data.addDependency.data.forEach((newDep) => {
      const newRowData = rowData.filter((eId) => parseInt(eId.id, 10) === newDep.dependsOn);
      const IntEpicID = newRowData[0].epicId.replace(/[^0-9]/g, '');
      if (newRowData.length !== 0) {
        const depends = {
          summary: newRowData[0].summary,
          description: newRowData[0].description,
          epicId: IntEpicID,
          id: newRowData[0].id,
          __typename: 'Epic',
        };
        addDependencyList.push({ depends, id: newDep.id, __typename: 'Dependencies' });
      }
    });

    const rowNode = e.node;
    const updatedData = { ...rowNode.data, dependency: [...rowNode.data.dependency, ...addDependencyList] };

    if (!undoEdits) setUndoEdits(true);

    rowNode.setData(updatedData);
    setAddDependsValue([]);
  };
  const onDeleteDependency = async (e) => {
    const DeleteDependency = { epicId: parseInt(e.data.id, 10), dependsOn: DeleteDepends };

    const variables = { input: DeleteDependency };
    await deleteDependency({ variables });
    const rowNode = e.node;
    const updatedData = {
      ...rowNode.data,
      dependency: [
        ...rowNode.data.dependency.filter((d) => !DeleteDepends.includes(parseInt(d.depends.id, 10))),
      ],
    };

    if (!undoEdits) setUndoEdits(true);

    rowNode.setData(updatedData);
    setDeleteDepends([]);
  };

  const handleDescriptionSummaryUpdate = async (e) => {
    const updateEpicData = {
      id: parseInt(e.data.id, 10),
      summary: e.data.summary,
      description: e.data.description,
    };
    const variables = { input: updateEpicData };
    await updateEpic({ variables });
    if (!undoEdits) setUndoEdits(true);
    setFetch(!fetch);
  };

  const handleTeamMembersUpdate = async (getSkillMembersData, e) => {
    const { data: fullstackSkillData } = await skillMembers({
      variables: {
        skillId: 11,
        projectId: parseInt(projectId, 10),
      },
    });

    const isFullStack = getSkillMembersData.getProjectSkill.members + fullstackSkillData.getProjectSkill.members;

    if (
      e.column.colId === 'frontendTeammembers' &&
      getSkillMembersData.getProjectSkill.members !== 0 &&
      fullstackSkillData.getProjectSkill.members !== 0 &&
      e.value > isFullStack
    ) {
      setTeamMemberMessage(`There are only ${isFullStack} Frontend in this project`);
      setOpenDialog(true);
    } else if (
      e.column.colId === 'backendTeammembers' &&
      getSkillMembersData.getProjectSkill.members !== 0 &&
      fullstackSkillData.getProjectSkill.members !== 0 &&
      e.value > isFullStack
    ) {
      setTeamMemberMessage(`There are only ${isFullStack} Backend in this project`);
      setOpenDialog(true);
    } else if (
      getSkillMembersData.getProjectSkill.members === 0 &&
      (getSkillMembersData.getProjectSkill.skillName === 'Front End' ||
        getSkillMembersData.getProjectSkill.skillName === 'Back End') &&
      e.value > fullstackSkillData.getProjectSkill.members
    ) {
      setTeamMemberMessage(
        `There are only ${fullstackSkillData.getProjectSkill.members} 
        Fullstack developers (BACKEND || FRONTEND) in this project`,
      );
      setOpenDialog(true);
    } else if (
      (e.column.colId === 'backendTeammembers' || e.column.colId === 'frontendTeammembers') && e.value <= isFullStack) {
      setCurrentSkillCellEvent(e);
    } else {
      setTeamMemberMessage(`There are only ${getSkillMembersData.getProjectSkill.members}
          ${getSkillMembersData.getProjectSkill.skillName} in this project`);
      setOpenDialog(true);
    }
  };

  const handleSkillUpdate = async (e) => {
    if (!open) {
      const { data: getSkillMembersData } = await skillMembers({
        variables: {
          skillId: parseInt(e.column.colDef.id, 10),
          projectId: parseInt(projectId, 10),
        },
      });
      if (e.column.colId.includes('Teammembers') && e.value > getSkillMembersData.getProjectSkill.members) {
        await handleTeamMembersUpdate(getSkillMembersData, e);
      } else {
        setOpenDialog(false);
        setCurrentSkillCellEvent(e);
        setTrigger(true);
      }
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (trigger) {
      if (!showTimelinePrompt) {
        setOpen(true);
      } else {
        handleClickOpen(currentSkillCellEvent);
      }
      setTrigger(false);
    }
  }, [trigger, currentSkillCellEvent]);

  const onUpdate = async (e) => {
    setIsLoading(true);
    try {
      if (e.column.colId === 'description' || e.column.colId === 'summary') {
        await handleDescriptionSummaryUpdate(e);
      } else if (skillList.includes(e.column.colId)) {
        await handleSkillUpdate(e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onCellKeyPressHandler = async (e) => {
    if (e?.column?.colId === 'dependency') {
      if (addDependsValue.length > 0) {
        onAddDependencyEpic(e);
      }
      if (DeleteDepends.length > 0) {
        onDeleteDependency(e);
      }
    } else if (e?.column?.colId === 'summary' || e?.column?.colId === 'description') {
      if (Object.keys(editEpicData).length === 0) {
        return;
      }
      setIsLoading(true);
      const variables = { input: editEpicData };
      await updateEpic({ variables });
      if (!undoEdits) setUndoEdits(true);
      setFetch(!fetch);
    }
  };

  const getNewRank = (list, oldIndex, newIndex) => {
    if (newIndex === 0) {
      const firstItem = list[0].sequence;
      return LexoRank.parse(firstItem).genPrev().toString();
    }
    if (newIndex === list.length - 1) {
      const lastItem = list[list.length - 1].sequence;
      return LexoRank.parse(lastItem).genNext().toString();
    }

    const over = LexoRank.parse(list[newIndex].sequence);
    let second = LexoRank.parse(list[newIndex + (newIndex < oldIndex ? -1 : 1)].sequence);

    // Ensure the ranks are distinct
    if (over.toString() === second.toString()) {
      second = second.genNext();
    }

    return over.between(second).toString();
  };

  const StateClear = () => {
    setAddDependsValue([]);
  };
  const onBtStopEditing = useCallback(() => {
    gridRef.current.api.stopEditing();
  }, []);

  const handleClose = () => {
    gridRef.current.api.undoCellEditing();
  };

  const handleDragEnd = async (e) => {
    setCurrentRank(e);
    if (sequenceData.activeIndex === e.overIndex) {
      return;
    }
    setPopup(true);
  };
  const onRowDragEnd = async () => {
    const currentNodes = [];
    gridRef?.current?.api?.forEachNode((node) => currentNodes?.push(node.data));
    await gridRef?.current?.api?.applyTransactionAsync({ remove: currentNodes });
    await gridRef?.current?.api?.applyTransactionAsync({ add: sequenceData.prevGridData });
  };

  const popupClose = async () => {
    onRowDragEnd();
    setPopup(false);
  };

  const popupOpen = async () => {
    const active = sequenceData.activeIndex;
    const gridData = sequenceData.prevGridData;
    const over = currentRank.overIndex;
    if (active !== over) {
      if (active === -1 || over === -1) {
        popupClose();
        return;
      }
      const itemsToUpdate = [];
      let newSequence = '';
      gridApi.forEachNodeAfterFilterAndSort((rowNode, index) => {
        if (over === index) {
          const { data: updatedRow } = rowNode;
          newSequence = getNewRank(gridData, active, over).toString();
          updatedRow.sequence = newSequence;
          itemsToUpdate.push(updatedRow);
        }
      });
      const variables = {
        input: {
          id: currentRank.node.data.id,
          projectId,
          sequence: newSequence,
        },
      };
      await updateSequence({ variables });
      if (!undoEdits) setUndoEdits(true);
      gridApi.applyTransaction({ update: itemsToUpdate });
    }
    setPopup(false);
  };

  const columnWidth = (e) => {
    const widthObj = {};
    e.columnApi.columnModel.displayedColumns.forEach((data) => {
      widthObj[data.colId] = data.actualWidth;
    });
    localStorage.setItem('Width', JSON.stringify(widthObj));
  };
  const openSprintSettings = () => {
    const elements = document.getElementsByClassName('btn-sprint');
    elements[0].click();
  };

  const openAlertPanel = () => {
    const elements = document.getElementsByClassName('btn-click');
    elements[0].click();
  };

  useEffect(() => {
    if (isLoading === false && showEpicTour === true) {
      if (startDate === null && duration === null) {
        const sprintTourOptions = {
          options: {
            darkLayerPersistence: true,
          },
          tips: [
            {
              title: '👆 Configure Sprint Settings',
              description: 'Click here to add sprint start date and duration',
              selector: '.setting-btn',
              x: 50,
              y: 70,
              offx: 0,
              offy: 0,
              position: 'bottom',
            },
            {
              title: 'Save Sprint Settings',
              description: 'Please add sprint start date and duration and save the settings',
              selector: '.setting-btn',
              x: -1010,
              y: 1240,
              offx: 0,
              offy: 0,
              position: 'bottom',
              onSelected: openSprintSettings,
            },
          ],
        };
        try {
          window.ProductTourJS.init(sprintTourOptions);
          window.ProductTourJS.start();
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [isLoading, startDate, duration]);

  useEffect(() => {
    if (isLoading === false && showEpicTour === true && startDate !== null && duration !== null) {
      const epicTourOptions = {
        options: {
          darkLayerPersistence: true,
          next: 'Next',
          prev: 'Previous',
          finish: 'Okay',
        },
        tips: [
          {
            title: 'Add Epic',
            description: ' Add new epics to your project from here',
            selector: '.btn-create',
            x: 10,
            y: 50,
            offx: 0,
            offy: 0,
            position: 'right',
            onSelected: true,
          },
        ],
      };
      if (rowData && rowData.length > 0) {
        const tipStatus = {
          title: 'Epic Status',
          description: 'As your project progresses, epic status will be displayed here. ',
          selector: '.search-input',
          x: 105,
          y: 290,
          offx: 0,
          offy: 0,
          position: 'right',
        };
        epicTourOptions.tips.push(tipStatus);
        const tipEstimate = {
          title: 'Add Estimate',
          description: 'In these columns you can provide estimates for your epics',
          selector: '.toggle-expand-btn',
          x: 1380,
          y: 290,
          offx: 0,
          offy: 0,
          position: 'left',
        };
        epicTourOptions.tips.push(tipEstimate);
        const tipShare = {
          title: 'Share Project',
          description: 'Invite other users to collaborate on your project here',
          selector: '.btn-share',
          x: 50,
          y: 80,
          offx: 0,
          offy: 0,
          position: 'bottom',
          onSelected: true,
        };
        epicTourOptions.tips.push(tipShare);
        const tipAlerts = {
          title: 'Alerts',
          description: 'Notifications and alerts will appear here',
          selector: '.alert-btn',
          x: -290,
          y: 340,
          offx: 0,
          offy: 0,
          position: 'left',
          onSelected: openAlertPanel,
        };
        epicTourOptions.tips.push(tipAlerts);
        epicTourOptions.tips.splice(0, 1);
      }
      try {
        window.ProductTourJS.init(epicTourOptions);
        window.ProductTourJS.start();
        if (epicTourOptions.tips.length !== 1) {
          setEpicTour(false);
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [isLoading, rowData]);

  useEffect(() => {
    const savedState = localStorage.getItem(SHOW_TIMELINE_PROMPT);
    if (savedState) {
      setShowTimelinePrompt(JSON.parse(savedState));
    }
  }, []);

  return (
    <>
      <div className="page-header" style={{ padding: isHidden ? '10px 0' : '16px 28px' }}>
        <div className="header-layout" style={{ display: isHidden ? 'none' : '' }}>
          <div className="header-search-and-text-wrap">
            <div className="top-search-btn">
              <TextField
                className="search-input"
                placeholder="Search"
                type="search"
                onChange={onFilterTextBoxChanged}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      style={{ color: 'rgba(0, 0, 0, 0.4)' }}
                      position="start"
                      component="div"
                    >
                      <SearchIcon className="search-icon" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <Button className={`toggle-expand-btn ${wrapText ? 'wrap-open' : ''}`} onClick={showSummary}>
              <WrapTextIcon className="wraptext-ic" />
            </Button>
          </div>
        </div>
      </div>

      <div style={CONTAINER_STYLE}>
        <ClickAwayListener onClickAway={onBtStopEditing}>
          <div
            className="ag-theme-alpine"
            style={{
              width: '100%',
              height: `min(${(((rowData.length + 1) * ROW_HEIGHT))}px, calc(100% + 2px))`,
            }}
          >
            <AgGridReact
              style={{
                width: '100%',
              }}
              className="ag-theme-custom-react ag-custom-epic-table"
              gridOptions={{
                suppressNoRowsOverlay: true,
                rowHeight: ROW_HEIGHT,
                headerHeight: 40,
                groupHeaderHeight: 0,
                defaultColDef: {
                  minWidth: 60,
                  singleClickEdit: true,
                  suppressMovable: true,
                  suppressCellClickSelection: true,
                },
              }}
              onColumnResized={columnWidth}
              rowBuffer
              sizeColumnsToFit
              rowData={rowData}
              ref={gridRef}
              columnDefs={columnDefs}
              animateRows
              rowDragManaged
              onDragStarted={() => {
                setIsDragStarted(true);
                isDragStartedRef.current = true;
              }}
              onRowDragEnter={(e) => {
                if (isDragStartedRef.current === true) {
                  const gridData = [];
                  gridApi.forEachNode((node) => gridData.push(node.data));
                  setSequenceData({ activeIndex: e.overIndex, prevGridData: gridData });
                  setIsDragStarted(false);
                  isDragStartedRef.current = false;
                }
              }}
              onDragStopped={(e) => {
                if (e.target.className === 'ag-header-cell-resize') {
                  return;
                }
                if (isValidDragEndRef.current === true) {
                  setIsValidDragEnd(false);
                } else {
                  onRowDragEnd();
                }
              }}
              onRowDragEnd={(e) => {
                isValidDragEndRef.current = true;
                setIsValidDragEnd(true);
                setIsDragStarted(false);
                isDragStartedRef.current = false;
                handleDragEnd(e, sequenceData.activeIndex, sequenceData.prevGridData);
              }}
              onCellEditingStarted={StateClear}
              onCellKeyPress={onCellKeyPressHandler}
              onCellValueChanged={onUpdate}
              suppressDragLeaveHidesColumns
              undoRedoCellEditing
              undoRedoCellEditingLimit={1}
              suppressScrollWhenPopupsAreOpen
              onGridReady={(gridParams) => {
                setGridApi(gridParams.api);
              }}
            />
          </div>
        </ClickAwayListener>
        <ClickAwayListener
          mouseEvent="onMouseDown"
          touchEvent="onTouchStart"
          onClickAway={handleClickAway}
        >
          <Box sx={{ position: 'relative', width: '100%', height: '60px' }}>
            {showAddButton ? (
              <div
                style={{
                  display: isHidden ? 'none' : '',
                }}
                className="btn-create"
              >
                <Button
                  primary
                  text
                  className="btn-add"
                  id="btn"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={onAddButtonClick}
                >
                  {btnName}
                </Button>
              </div>
            ) : (
              <div className="epic-summary-input">
                <TextField
                  id="summary"
                  className="custom-form-input"
                  component="div"
                  placeholder="What is the Epic Summary?"
                  onKeyPress={onSummaryKeyPress}
                  onKeyUp={onSummaryKeyUp}
                  helperText={errorMessage}
                  required
                  autoFocus
                />
              </div>
            )}
          </Box>
        </ClickAwayListener>
      </div>
      <span>
        {open && !showTimelinePrompt && (
          <Dialog
            className="custom-modal-dialog confirmation-modal-dialog"
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent className="dialog-content">
              <span id="alert-dialog-description">
                This change will update the timeline
              </span>
              <span id="alert-dialog-description">
                Do you want to proceed?
              </span>
              <div className="checkbox-container">
                <FormControlLabel
                  className="form-item"
                  label={<span className="custom-label">Don&apos;t ask me again</span>}
                  control={<Checkbox
                    size="small"
                    inputRef={checkboxRef}
                  />}
                />
              </div>
            </DialogContent>
            <DialogActions className="dialog-actions">
              <Button primary outlined onClick={handleClose} disableFocusRipple>Cancel</Button>
              <Button primary onClick={handleClickOpen} autoFocus disableFocusRipple>Continue</Button>
            </DialogActions>
          </Dialog>
        )}
      </span>
      <span>
        <Dialog
          className="custom-modal-dialog confirmation-modal-dialog"
          open={popup}
          onClose={popupClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent className="dialog-content">
            <DialogContentText id="alert-dialog-description">
              This change will update the timeline.
            </DialogContentText>
            <DialogContentText id="alert-dialog-description">
              Do you want to proceed?
            </DialogContentText>
          </DialogContent>
          <DialogActions className="dialog-actions">
            <Button primary outlined onClick={popupClose}>Cancel</Button>
            <Button primary onClick={popupOpen}>Continue</Button>
          </DialogActions>
        </Dialog>
      </span>
      <span>
        {openDialog && (
          <AlertTeamMembersDialog
            open={openDialog}
            dialogClose={dialogClose}
            message={teamMemberMessage}
          />
        )}
      </span>
    </>
  );
};

EpicList.propTypes = propTypes;
EpicList.defaultProps = defaultProps;
export default EpicList;
